import { lazy, StrictMode, Suspense, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router';
import { AuthProvider, useAuth } from './auth/AuthProvider.tsx';
import { StepUpProvider } from './auth/StepUp.tsx';
import { OfflineBanner } from './components/OfflineBanner.tsx';
import { loadConfig, setRuntimeConfig } from './config.ts';
import { initApi } from './lib/api.ts';
import { listenForRenewals, registerServiceWorker } from './lib/push.ts';
import { initSupabase } from './lib/supabase.ts';
import { applyStoredTheme } from './lib/theme.ts';
import { Account } from './routes/Account.tsx';
import { AccountKeys } from './routes/AccountKeys.tsx';
import { AuthConfirm, AuthRefresh } from './routes/AuthPages.tsx';
import { Home } from './routes/Home.tsx';
import { Login } from './routes/Login.tsx';
import { Setup } from './routes/Setup.tsx';
import { Welcome } from './routes/Welcome.tsx';
import './styles.css';

// The Host Manager is only loaded for admins.
const AdminLayout = lazy(() =>
  import('./admin/AdminLayout.tsx').then((m) => ({ default: m.AdminLayout })),
);
const Overview = lazy(() => import('./admin/Overview.tsx').then((m) => ({ default: m.Overview })));
const Apps = lazy(() => import('./admin/Apps.tsx').then((m) => ({ default: m.Apps })));
const Users = lazy(() => import('./admin/Users.tsx').then((m) => ({ default: m.Users })));
const Remote = lazy(() => import('./admin/Remote.tsx').then((m) => ({ default: m.Remote })));
const Ai = lazy(() => import('./admin/Ai.tsx').then((m) => ({ default: m.Ai })));
const ApiKeys = lazy(() => import('./admin/ApiKeys.tsx').then((m) => ({ default: m.ApiKeys })));
const Catalog = lazy(() => import('./admin/Catalog.tsx').then((m) => ({ default: m.Catalog })));
const Hardware = lazy(() => import('./admin/Hardware.tsx').then((m) => ({ default: m.Hardware })));
const Library = lazy(() => import('./admin/Library.tsx').then((m) => ({ default: m.Library })));
const NucBox = lazy(() => import('./admin/NucBox.tsx').then((m) => ({ default: m.NucBox })));
const Updates = lazy(() => import('./admin/Updates.tsx').then((m) => ({ default: m.Updates })));
const Backups = lazy(() => import('./admin/Backups.tsx').then((m) => ({ default: m.Backups })));
const Wiki = lazy(() => import('./admin/Wiki.tsx').then((m) => ({ default: m.Wiki })));
const Guide = lazy(() => import('./admin/Guide.tsx').then((m) => ({ default: m.Guide })));
const Upload = lazy(() => import('./admin/Upload.tsx').then((m) => ({ default: m.Upload })));
const Suite = lazy(() => import('./admin/Suite.tsx').then((m) => ({ default: m.Suite })));
const Games = lazy(() => import('./routes/Games.tsx').then((m) => ({ default: m.Games })));
const GameProfile = lazy(() =>
  import('./routes/Games.tsx').then((m) => ({ default: m.GameProfile })),
);
const Workshop = lazy(() => import('./admin/Workshop.tsx').then((m) => ({ default: m.Workshop })));

function RequireSession() {
  const { session, codePending, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  // Without the authenticator code the database shows nothing yet; the login page asks for it.
  if (!session || codePending) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return (
    <StepUpProvider>
      <OfflineBanner />
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </StepUpProvider>
  );
}

/**
 * `/logout`: apps send the browser here to sign out, so the portal can also switch off this
 * device's push subscription and forget its offline copies (ADR 0005).
 */
function Logout() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPortal = (location.state as { fromPortal?: boolean } | null)?.fromPortal === true;
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Only the portal and its apps may sign the user out this way (no logout from other sites).
    if (!fromPortal && !fromPlatform(document.referrer)) {
      navigate('/', { replace: true });
      return;
    }
    void signOut().finally(() => navigate('/login', { replace: true }));
  }, [signOut, navigate, fromPortal]);
  return null;
}

function fromPlatform(referrer: string): boolean {
  if (!referrer) return false;
  try {
    const from = new URL(referrer);
    const home = location.hostname;
    return (
      from.hostname === home || from.hostname.endsWith(`.${home}`) || from.hostname === 'localhost'
    );
  } catch {
    return false;
  }
}

const router = createBrowserRouter([
  { path: '/logout', element: <Logout /> },
  { path: '/login', element: <Login /> },
  { path: '/setup', element: <Setup /> },
  { path: '/auth/confirm', element: <AuthConfirm /> },
  { path: '/auth/refresh', element: <AuthRefresh /> },
  {
    element: <RequireSession />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/welcome', element: <Welcome /> },
      { path: '/account', element: <Account /> },
      { path: '/account/keys', element: <AccountKeys /> },
      { path: '/games', element: <Games /> },
      { path: '/games/:slug', element: <GameProfile /> },
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Overview /> },
          { path: 'apps', element: <Apps /> },
          { path: 'upload', element: <Upload /> },
          { path: 'backups', element: <Backups /> },
          { path: 'updates', element: <Updates /> },
          { path: 'wiki', element: <Wiki /> },
          { path: 'wiki/:slug', element: <Wiki /> },
          { path: 'guide', element: <Guide /> },
          { path: 'catalog', element: <Catalog /> },
          { path: 'users', element: <Users /> },
          {
            path: 'hardware',
            element: <Hardware />,
            children: [
              { index: true, element: <NucBox /> },
              { path: 'remote', element: <Remote /> },
              { path: 'library', element: <Library /> },
            ],
          },
          { path: 'remote', element: <Navigate to="/admin/hardware/remote" replace /> },
          { path: 'nucbox', element: <Navigate to="/admin/hardware" replace /> },
          { path: 'library', element: <Navigate to="/admin/hardware/library" replace /> },
          { path: 'ai', element: <Ai /> },
          { path: 'api-keys', element: <ApiKeys /> },
          { path: 'suite', element: <Suite /> },
          { path: 'workshop', element: <Workshop /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

async function start() {
  applyStoredTheme();
  const config = await loadConfig();
  initSupabase(config);
  initApi(config);
  setRuntimeConfig(config);
  // Offline shell and push notifications (ADR 0005).
  void registerServiceWorker();
  listenForRenewals();
  const root = document.getElementById('root');
  if (!root) throw new Error('#root missing');
  createRoot(root).render(
    <StrictMode>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </StrictMode>,
  );
}

void start();
